import { useCallback, useEffect, useRef, useState } from "react";
import config from "../config/config";

/**
 * Detects when a newer build has been deployed while this tab is already open.
 *
 * HTTP cache-control headers (configured server-side, e.g. in nginx) stop a
 * *new* page load from picking up a stale index.html/bundle — but they can't
 * do anything for a tab that already has the old build's JS running in memory.
 * This hook closes that gap by re-fetching build/asset-manifest.json (CRA emits
 * this on every build, keyed by content hash) with `cache: "no-store"` so the
 * request always hits the network, then comparing the hashed main bundle path
 * against the one this tab started with.
 */
export function useBuildVersionWatcher({ enabled = true } = {}) {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const baselineRef = useRef(null);

  const checkForUpdate = useCallback(async () => {
    try {
      const base = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
      const res = await fetch(`${base}/asset-manifest.json`, {
        cache: "no-store",
      });
      if (!res.ok) return;

      const data = await res.json();
      const mainJs = data?.files?.["main.js"];
      if (!mainJs) return;

      if (baselineRef.current === null) {
        baselineRef.current = mainJs;
        return;
      }

      if (mainJs !== baselineRef.current) {
        setUpdateAvailable(true);
      }
    } catch {
      // Network hiccup / offline — don't nag the user over a failed check.
    }
  }, []);

  useEffect(() => {
    if (!enabled || process.env.NODE_ENV !== "production") return undefined;

    checkForUpdate();

    const intervalId = setInterval(checkForUpdate, config.buildVersionCheckIntervalMs);

    const onVisible = () => {
      if (document.visibilityState === "visible") checkForUpdate();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [enabled, checkForUpdate]);

  const reload = useCallback(() => {
    window.location.reload();
  }, []);

  return { updateAvailable, reload };
}

export default useBuildVersionWatcher;
